import { type fabric } from 'fabric';
import ApplicationController, { randomId } from '@aha-app/mvc';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import * as Sentry from '@sentry/browser';
import Debug from 'debug';
import { z } from 'zod';
// @ts-expect-error See app/assets/javascripts/faye_client
import { createFayeClient } from 'javascripts/faye_client';
import Serialization from '../collaboration/Serialization';
import StreamParser from '../components/Assistant/StreamParser';
import { type JsonData } from '../components/Assistant/XMLWithEmbeddedJSONParser';
import ToolDelete from '../tools/ToolDelete';
import ToolSelectAll from '../tools/ToolSelectAll';
import { nullHrefsInJsonHTML } from '../utils/jsonLinkSanitizer';
import { mapShapeToAnother, validateAIResponse } from '../utils/jsonValidator';
import { zoomTo } from '../utils/zoom';
import WhiteboardController from './WhiteboardController';
import { SUMMARIZE_PROMPT_START } from '../components/Assistant/AssistantToolbar';
import { hasEmbeddedTextBehaviors } from '../objects/guards';

const debug = Debug('whiteboard:assistant');
const INPUT_TOKEN_LIMIT = 80000; // Current backend limit for input is 100k, but we are leaving margin for the system prompt
const CHARS_PER_TOKEN = 4; // rough estimate of chars per token

const DEVICE_MARGIN = 20;

class IdMapper {
  idMap: Map<string, string>;

  constructor() {
    this.idMap = new Map<string, string>();
  }

  replaceId(object: { id: string }) {
    const oldId = object.id;
    object.id = randomId();
    this.idMap[oldId] = object.id;
  }

  updateId(object: { id: string }) {
    if (this.idMap[object.id]) {
      object.id = this.idMap[object.id];
    }
  }

  idFor(oldId) {
    return this.idMap[oldId] || oldId;
  }

  containsNewId(id) {
    return Object.values(this.idMap).includes(id);
  }
}

interface AssistantControllerState {
  waitingForResponse: boolean;
}

interface Interaction {
  request: string;
  response: string;
}

class AssistantController extends ApplicationController<AssistantControllerState> {
  static initialState: AssistantControllerState = {
    waitingForResponse: false,
  };

  // The whiteboard that this assistant is part of.
  whiteboardController: WhiteboardController;

  // History of the requests and responses so we can use them for context.
  interactions: Interaction[] = [];

  assistantAbortController: AbortController;
  generationFromFaye: boolean;
  generationResponseChunks: string[];
  generationTouchedObjectIds: RecordId[];
  generationParser: StreamParser;
  generationPrompt: string;
  generationFayeSubscription: any;
  generationAtLeastOneValidChunkReceived: boolean;
  generationDeviceMoved: boolean;

  async initialize() {
    this.whiteboardController =
      this.findControllerInstance(WhiteboardController)!;
  }

  generationStarted() {
    this.state.waitingForResponse = true;
  }

  async actionStopGeneration() {
    if (this.assistantAbortController) {
      this.assistantAbortController.abort();
    }

    if (this.generationFromFaye) {
      this.generationFromFaye = false;
      this.generationFayeSubscription.cancel();
    }

    this.generationDeviceMoved = false;
    this.state.waitingForResponse = false;
  }

  async actionGenerateResponse(prompt: string, image: File) {
    this.generationStarted();

    if (prompt && !image) {
      this.storePromptInHistory(prompt);

      this.generationPrompt = prompt;
    }

    // Send the request and selection and context to the server.
    const selection = this.whiteboardController.toolbarObject()
      ? this.whiteboardController.toolbarObject().getObjects()
      : null;

    let requestBody, formData;
    const currentObjects = this.whiteboardToSimpleFormat(selection);

    // Process the history to keep only the most relevant context
    const prunedHistory = this.pruneHistory(currentObjects);

    const requestContext = {
      history: prunedHistory,
      currentObjects,
    };

    if (image) {
      // FormData is used to send the image file to the server.
      formData = new FormData();
      formData.append('image', image);
      formData.append('context', JSON.stringify(requestContext));
      formData.append('type', 'whiteboard_assistant');
    } else {
      requestBody = {
        type: 'whiteboard_assistant',
        prompt,
        context: requestContext,
      };
    }

    this.generationResponseChunks = [];
    this.generationTouchedObjectIds = [];
    this.generationAtLeastOneValidChunkReceived = false;

    // AIs are not great at generating unique IDs, so we replace the ones they
    // provide us with with our own. This is a map of ids the AI gives us to
    // the ones we generate.
    const newIdMap = new IdMapper();

    this.generationParser = new StreamParser((change, value) => {
      const wasValidChunk = this.handleStreamChunk(change, value, newIdMap);
      this.generationAtLeastOneValidChunkReceived ||= wasValidChunk;
    });

    const headers = {
      'X-CSRF-Token': window.csrfToken(),
    };
    if (!formData) {
      headers['Content-Type'] = 'application/json';
    }

    this.assistantAbortController = new AbortController();
    const abortSignal = this.assistantAbortController.signal;

    fetchEventSource('/assistances.json', {
      signal: abortSignal,
      method: 'POST',
      headers,
      body: formData || JSON.stringify(requestBody),
      openWhenHidden: true,
      onmessage: msg => {
        switch (msg.event) {
          case 'background': {
            this.actionSubscribeToAssistantStream(
              (JSON.parse(msg.data) as any).message
            );
            break;
          }
          default: {
            if (!this.generationFromFaye) {
              this.onAssistantMessage(msg);
            }
            break;
          }
        }
      },
      onerror: e => {
        this.actionStopGeneration();
        this.whiteboardController.actionShowMessage(
          'There was an error submitting your AI request.'
        );
        console.error('An error occurred in the assistant.', e);
        throw e;
      },
    }).catch(e => {
      const msg = `An error was caught in the assistant: ${e}`;
      console.error(msg, e);
      throw new Error(msg);
    });
  }

  async actionSubscribeToAssistantStream(data) {
    this.generationFromFaye = true;

    const tokenInfo = data as {
      channel: string;
      token: string;
      tokenExpiry: string;
    };
    const { channel, token, tokenExpiry } = tokenInfo;

    const fayeClient = createFayeClient();
    fayeClient.addExtension({
      outgoing: (message, callback) => {
        if (message.channel === '/meta/subscribe') {
          if (message.subscription === channel) {
            message.ext = {};
            message.ext.token = token;
            message.ext.token_expiry = tokenExpiry;
          }
        } else if (message.channel === channel) {
          message.ext = {};
          message.ext.token = token;
          message.ext.token_expiry = tokenExpiry;
        }
        callback(message);
      },
    });

    this.generationFayeSubscription = fayeClient.subscribe(channel, msg => {
      this.onAssistantMessage(msg);
    });
  }

  onAssistantMessage(msg) {
    const { waitingForResponse } = this.state;
    if (!waitingForResponse) {
      return;
    }

    const parsedMsgData: any =
      msg.data && typeof msg.data === 'string'
        ? JSON.parse(msg.data)
        : msg.data;

    switch (msg.event) {
      case 'text': {
        this.generationParser.parse(parsedMsgData.text);
        this.generationResponseChunks.push(parsedMsgData.text);
        break;
      }
      case 'done': {
        this.actionStopGeneration();
        const responseText = this.generationResponseChunks.join('');
        this.interactions.push({
          request: this.generationPrompt,
          response: responseText,
        });
        if (!this.generationAtLeastOneValidChunkReceived) {
          console.error('No valid actions received from AI');
          this.whiteboardController.actionShowMessage(
            'There was an error in the AI response. Please try a different prompt.'
          );
        }

        this.whiteboardController.undoManager.completeUndoGroup();
        this.zoomToObjects(this.generationTouchedObjectIds);

        break;
      }
      case 'error': {
        this.actionStopGeneration();

        if (msg.data && parsedMsgData.status === 451) {
          console.error('AI request was blocked', parsedMsgData.message);
          this.whiteboardController.actionShowMessage(
            'This AI response may contain inappropriate content. Try editing your prompt to receive a different reply.'
          );

          this.handleDeletes(this.generationTouchedObjectIds);
          break;
        }

        if (msg.data && parsedMsgData.status === 413) {
          this.whiteboardController.actionShowMessage(
            'The contents of the whiteboard are too large to use the AI assistant, select something to only send that.'
          );
          break;
        }

        if (msg.data) {
          this.whiteboardController.actionShowMessage(parsedMsgData.message);
          console.error(
            'An error occurred',
            parsedMsgData.status,
            parsedMsgData.message
          );
        } else {
          console.error(
            'An error message was found while attempting to connect.'
          );

          this.whiteboardController.actionShowMessage(
            'There was an error communicating your AI request.'
          );
        }

        break;
      }
    }
  }

  handleStreamChunk(
    change: string,
    value: JsonData,
    newIdMap: IdMapper
  ): boolean {
    debug(`Received change ${change} with value ${JSON.stringify(value)}`);

    const { error, data: operation } = z
      .enum(['create', 'update', 'delete', 'select'])
      .safeParse(change);
    if (error) {
      console.error('Unknown change type', change);
      return false;
    }

    switch (operation) {
      case 'create': {
        return this.handleCreate(value, newIdMap);
      }

      case 'update': {
        return this.handleUpdate(value, newIdMap);
      }
      case 'select': {
        if (!Array.isArray(value)) {
          console.error('Select value is not an array', value);
          return false;
        }

        const selectValues = value.map(id => newIdMap.idFor(id));
        this.handleSelection(selectValues);

        return true;
      }
      case 'delete': {
        if (!Array.isArray(value)) {
          console.error('Delete value is not an array', value);
          return false;
        }

        const deleteValues = value.map(id => newIdMap.idFor(id));
        this.handleDeletes(deleteValues);

        return true;
      }
      default: {
        const _exhaustivenessCheck: never = operation;
        return _exhaustivenessCheck;
      }
    }
  }

  handleUpdate(change: JsonData, newIdMap: IdMapper): boolean {
    if (Array.isArray(change)) {
      return false;
    }
    nullHrefsInJsonHTML(change);

    const { id: incomingId, ...changedValues } = change;

    const id = newIdMap.idFor(incomingId) || incomingId;

    const object =
      this.whiteboardController.dataStoreObjects.findRecordById(id);

    if (!object) {
      debug(
        `Object ${id} not found in data store for assistant change ${JSON.stringify(
          change
        )}.`
      );
      return false;
    }

    const validatedObject = this.validateUpdate(object, changedValues);

    if (!validatedObject) {
      console.error('Invalid update chunk', changedValues);
      return false;
    }

    // It would be very weird for the response to create an object and then
    // update it in the same response, but it could happen.
    newIdMap.updateId(object);

    for (const [key, value] of Object.entries(validatedObject)) {
      if (object.serializedKeys.includes(key)) {
        // If the object has a container, replace the container id with the new generated id of the parent container
        if (key === 'container') {
          const newContainerId = newIdMap.idFor(value);
          if (newContainerId) {
            object.set(key, newContainerId);
          }
        } else {
          object.set(key, value);
        }
      }
    }

    this.whiteboardController.canvas.requestRenderAll();

    return true;
  }

  private validateUpdate(fabricObject, changedValues) {
    const mergedObject = fabricObject.toJSON();

    for (const [key, value] of Object.entries(changedValues)) {
      if (fabricObject.serializedKeys.includes(key)) {
        mergedObject[key] = value;
      }
    }

    const validatedJsonResponse = validateAIResponse(mergedObject);

    if (!validatedJsonResponse.success) {
      // To test this just ask 'update the circle with an invalid color'
      Sentry.captureMessage('AI update response failed validation', {
        contexts: {
          aiData: {
            changedValues: JSON.stringify(changedValues),
            errors: JSON.stringify(validatedJsonResponse.errors),
          },
        },
      });
      return false;
    }

    if (validatedJsonResponse.data) {
      return validatedJsonResponse.data;
    } else {
      console.error(
        'Validation response did not have data',
        validatedJsonResponse
      );
      return false;
    }
  }

  private validateCreate(responseValue: Record<string, any>) {
    debug('Validating create object', responseValue);

    const validatedJsonResponse = validateAIResponse(responseValue);

    if (!validatedJsonResponse.success) {
      console.error('Failed to validate JSON response', validatedJsonResponse);
      // To test this just ask 'create a circle with an invalid color'
      Sentry.captureMessage('AI create response failed validation', {
        contexts: {
          aiData: {
            responseValue: JSON.stringify(responseValue),
            errors: JSON.stringify(validatedJsonResponse.errors),
          },
        },
      });
      return null;
    } else {
      debug('Response passed validation');

      if (validatedJsonResponse.data) {
        return validatedJsonResponse.data;
      } else {
        console.error('No data in validated response', validatedJsonResponse);
        return null;
      }
    }
  }

  handleCreate(llmObject: JsonData, newIdMap: IdMapper): boolean {
    let top = 0;

    const zShouldMap = z
      .object({ type: z.enum(['wireframe', 'text']) })
      .passthrough();

    const zObjectWithType = z.object({ type: z.string() }).passthrough();

    let parsed:
      | ReturnType<typeof mapShapeToAnother>
      | z.infer<typeof zObjectWithType>;

    const shouldMap = zShouldMap.safeParse(llmObject);
    if (shouldMap.success) {
      parsed = mapShapeToAnother(shouldMap.data);
    } else {
      const hasATypeProperty = zObjectWithType.safeParse(llmObject);
      if (hasATypeProperty.success) {
        parsed = hasATypeProperty.data;
      }
    }

    if (!parsed) {
      return false;
    }

    const newObject = {
      ...this.defaultsFor(parsed),
      ...parsed,
    };

    if (!newObject.id) {
      newObject.id = randomId();
    }

    nullHrefsInJsonHTML(newObject);

    // This is only a fallback if the AI does not pass us a value
    if (!newObject.top) {
      newObject.top = top;
      top += 70;
    }

    const validatedNewObject = this.validateCreate(newObject);

    if (!validatedNewObject) {
      return false;
    }

    newIdMap.replaceId(validatedNewObject as { id: string });
    const createdObjectId = validatedNewObject.id;

    // If the object has a container, replace the container id with the new generated id of the parent container
    if (typeof validatedNewObject.container === 'string') {
      debug('Replacing container ids');
      const newContainerId = newIdMap.idFor(validatedNewObject.container);
      if (newContainerId) {
        validatedNewObject.container = newContainerId;

        this.checkIfNewObjectIsOutsideDevice(
          newContainerId,
          validatedNewObject
        );
      }
    }

    // If creating a group object, we need to replace the ids of the objects in the group
    if (
      'objects' in validatedNewObject &&
      Array.isArray(validatedNewObject.objects)
    ) {
      debug('Replacing group object ids');
      validatedNewObject.objects = validatedNewObject.objects.map(objectId =>
        newIdMap.idFor(objectId)
      );
    }

    const newDoc = { objects: [validatedNewObject] };

    Serialization.restoreDocument(
      this.whiteboardController,
      newDoc,
      // Pass in all the current ids on the document
      this.whiteboardController.canvas.getObjects().map(object => object.id)
    );

    if (createdObjectId) {
      this.generationTouchedObjectIds.push(createdObjectId);

      // If its a new device wireframe jump the zoom to the device instantly
      // to allow the user to follow along better
      if (validatedNewObject.type === 'device') {
        this.zoomToObjects([createdObjectId]);
      }
    }

    return true;
  }

  defaultsFor(
    llmObject:
      | { type: 'shape'; shapeName: string }
      | { type: 'wireframe'; wireframeName: string }
      | { type: 'device'; deviceName: string }
      | { type: string; [key: string]: any }
  ) {
    switch (llmObject.type) {
      case 'shape':
        return this.whiteboardController.defaultsManager.for(
          'shape',
          llmObject.shapeName
        );

      case 'wireframe':
        return this.whiteboardController.defaultsManager.for(
          'wireframe',
          llmObject.wireframeName
        );

      case 'device':
        return this.whiteboardController.defaultsManager.for(
          'device',
          llmObject.deviceName
        );

      default:
        return this.whiteboardController.defaultsManager.for(llmObject.type);
    }
  }

  handleSelection(selection) {
    const dataStore = this.whiteboardController.dataStoreObjects;

    const objects = selection
      .map(id => dataStore.findRecordById(id))
      .filter(object => object);
    if (objects.length > 0) {
      ToolSelectAll.setSelection(this.whiteboardController.canvas, objects);
    }
  }

  handleDeletes(deletes) {
    const dataStore = this.whiteboardController.dataStoreObjects;

    const objects = deletes
      .map(id => dataStore.findRecordById(id))
      .filter(object => object);
    if (objects.length > 0) {
      ToolDelete.deleteObjects(objects, this.whiteboardController);
    }
  }

  // Convert the internal whiteboard format to our simplified JSON format to
  // send to the LLM.
  private whiteboardToSimpleFormat(selection: fabric.Object[] | null): any[] {
    // Check if we're using the whiteboard summarize prompt. This is a very simple
    // check against the start of the prompt. If the user modifies this, we can
    // assume that the user is doing something more than summarizing text content
    const isTextSummarization = this.generationPrompt?.includes(
      SUMMARIZE_PROMPT_START
    );

    // If using text summarization prompt, extract just the text content
    if (isTextSummarization) {
      return this.extractTextContentOnly(selection);
    }

    // Regular object serialization for other prompts
    const simpleObjects: any[] = [];

    const objects = selection || this.whiteboardController.canvas.getObjects();

    let emptyStickyCount = 0;
    objects.forEach((object: any) => {
      if (object.shapeName === 'note' && object.textContent === '') {
        emptyStickyCount++;
        // If there are more than 20 empty notes we skip them to save on token count
        if (emptyStickyCount > 20) {
          return;
        }
      }

      simpleObjects.push(object.toJSON());
    });

    // To shorten the token count we remove the default values from the objects
    const defaultsProps = {
      strokeWidth: [0, 1],
      strokeType: 0,
      textColor: '#000000',
      textContent: '',
      opacity: 1,
      strokeDouble: false,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      fontSize: 14,
      borderOpacity: 1,
      fill: ['#FFFFFF', 'transparent'],
      stroke: '#000000',
      label: {},
      placeholder: {},
      itemsData: {},
      locked: false,
      z: 1,
      textAlign: 'left',
      showFramed: true,
      reactions: {},
      container: null,
    };

    const unusedProps = ['authorId', 'maskType', 'showAuthor', 'wrapText'];

    simpleObjects.forEach(object => {
      for (const key in defaultsProps) {
        // IF the default props are and array we need to check if the value is in the array
        if (Array.isArray(defaultsProps[key])) {
          if (
            defaultsProps[key].includes(object[key]) ||
            object[key] === null
          ) {
            delete object[key];
          }
        } else if (
          object[key] === defaultsProps[key] ||
          object[key] === null ||
          (typeof object[key] === 'object' &&
            Object.keys(object[key]).length === 0)
        ) {
          delete object[key];
        }
      }

      unusedProps.forEach(prop => {
        delete object[prop];
      });
    });

    return simpleObjects;
  }

  // Extract only text content from whiteboard objects for the text summarization prompt
  private extractTextContentOnly(selection: fabric.Object[] | null): string[] {
    const objects = selection ?? this.whiteboardController.canvas.getObjects();
    const textItems: string[] = [];

    objects.forEach(object => {
      if (
        !hasEmbeddedTextBehaviors(object) ||
        object.plainText().trim() === ''
      ) {
        return;
      }

      textItems.push(object.plainText());
    });

    return textItems;
  }

  promptHistory() {
    const storedHistory = localStorage.getItem(
      `whiteboardAssistantPrompts.${this.whiteboardController.whiteboardId}`
    );

    if (storedHistory) {
      return JSON.parse(storedHistory) as string[];
    }

    return [];
  }

  private zoomToObjects(objectIds) {
    debug('Zooming to object ids', objectIds);

    const dataStore = this.whiteboardController.dataStoreObjects;

    const objects = objectIds
      .map(id => dataStore.findRecordById(id))
      .filter(object => object);

    debug('Zooming to objects', objects);
    if (objects.length > 0) {
      zoomTo(this.whiteboardController, objects, { maxZoom: 1 });
    }
  }

  private storePromptInHistory(prompt: string) {
    const history = this.promptHistory();

    history.push(prompt);

    localStorage.setItem(
      `whiteboardAssistantPrompts.${this.whiteboardController.whiteboardId}`,
      JSON.stringify(history)
    );
  }

  // Prune history in attempt to keep as much relevant context as possible while
  // staying within token input limits. We prioritize keeping the user prompts
  // and removing model output in previous interactions
  private pruneHistory(currentObjects): Interaction[] {
    if (this.interactions.length === 0) {
      return [];
    }

    const usedTokens =
      this.estimateTokens(this.generationPrompt ?? '') +
      this.estimateTokens(JSON.stringify(currentObjects));

    debug('current prompt and selection uses', usedTokens, 'tokens');
    // Calculate remaining token budget
    const remainingTokenBudget = Math.max(0, INPUT_TOKEN_LIMIT - usedTokens);

    // Process history from most recent to oldest in a single pass
    let tokenBudgetUsed = 0;
    const processedHistory: Interaction[] = [];

    // Iterate from most recent to oldest
    // Process remaining interactions from newest to oldest
    for (let i = this.interactions.length - 1; i >= 0; i--) {
      debug('pruneHistory processing interaction:', i);
      const interaction = this.interactions[i];

      // Estimate tokens for the full interaction
      const fullResponseTokens = this.estimateTokens(
        JSON.stringify(interaction)
      );

      // If we can fit the full interaction, add it. Likely this means we will
      // keep at most the most recent full interaction, and the rest will be
      // redacted
      if (tokenBudgetUsed + fullResponseTokens <= remainingTokenBudget) {
        debug(
          'Adding full interaction to processed history:',
          i,
          remainingTokenBudget,
          tokenBudgetUsed
        );
        processedHistory.push(interaction);
        tokenBudgetUsed += fullResponseTokens;
        continue;
      }

      // If full interaction doesn't fit, try redacted version
      const simplifiedInteraction = {
        request: interaction.request,
        response: '[omitted to save space]',
      };
      const simplifiedTokens = this.estimateTokens(
        JSON.stringify(simplifiedInteraction)
      );

      // If redacted version fits, add it
      if (tokenBudgetUsed + simplifiedTokens <= remainingTokenBudget) {
        debug(
          'Adding redacted interaction to processed history processed history:',
          i,
          remainingTokenBudget,
          tokenBudgetUsed
        );
        processedHistory.unshift(simplifiedInteraction);
        tokenBudgetUsed += simplifiedTokens;
      } else {
        // No more room for any interactions, stop processing
        debug(
          'No more room for interactions, stopping processing at interaction:',
          i,
          remainingTokenBudget,
          tokenBudgetUsed
        );
        break;
      }
    }

    return processedHistory;
  }

  checkIfNewObjectIsOutsideDevice(deviceId: RecordId, newObject) {
    // If the object is in a device that was newly created then check if this would go outside the device
    const device =
      this.whiteboardController.dataStoreObjects.findRecordById(deviceId);

    if (device && device.type === 'device') {
      if (
        newObject.left + newObject.width >
        device.left + device.width * device.scaleX
      ) {
        const newWidth =
          (newObject.left + newObject.width - device.left + DEVICE_MARGIN) /
          device.scaleX;
        debug('Applying new width to containing device', device, newWidth);
        device.width = newWidth;
        device.setCoords();
      }
      if (
        newObject.top + newObject.height * newObject.scaleY >
        device.top + device.height * device.scaleY
      ) {
        const newHeight =
          (newObject.top +
            newObject.height * newObject.scaleY -
            device.top +
            DEVICE_MARGIN) /
          device.scaleY;

        debug('Applying new height to containing device', device, newHeight);
        device.height = newHeight;
        device.setCoords();
      }
    }
  }

  private estimateTokens(input: string): number {
    return Math.ceil(input.length / CHARS_PER_TOKEN);
  }
}

export default AssistantController;
